import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";

export interface StockMovementSummaryRow {
  itemId: string;
  itemName: string;
  unit: string | null;
  quantityIn: number;
  quantityOut: number;
  adjustments: number;
  movementCount: number;
}

export interface StockLevelRow {
  stockId: string;
  itemId: string;
  itemName: string;
  unit: string | null;
  quantity: number;
  minExpiryDate: Date | null;
  hasExpiringItems: boolean;
}

export interface ReportsStockData {
  quantityIn: number;
  quantityOut: number;
  adjustments: number;
  lowStockCount: number;
  criticalStockCount: number;
  movementRows: StockMovementSummaryRow[];
  lowStockRows: StockLevelRow[];
  currentStockByItemId: Record<string, number>;
}

type StockMovementSelect = {
  item_id: string;
  type: string;
  quantity: number | null;
  items?: { id?: string | null; name?: string | null; unit?: string | null } | null;
};

type ProductStockSelect = {
  id: string;
  item_id: string;
  quantity: number | null;
  min_expiry_date: string | null;
  has_expiring_items: boolean | null;
  items?: { id?: string | null; name?: string | null; unit?: string | null } | null;
};

const LOW_STOCK_THRESHOLD = 10;

export function useReportsStock(dateFrom: Date | null, dateTo: Date | null) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-stock", clinicId, dateFrom?.toISOString(), dateTo?.toISOString()],
    queryFn: async (): Promise<ReportsStockData> => {
      if (!clinicId || !dateFrom || !dateTo) {
        return {
          quantityIn: 0,
          quantityOut: 0,
          adjustments: 0,
          lowStockCount: 0,
          criticalStockCount: 0,
          movementRows: [],
          lowStockRows: [],
          currentStockByItemId: {},
        };
      }

      const [movementsRes, stockRes] = await Promise.all([
        supabase
          .from("product_stock_movements")
          .select("item_id, type, quantity, items:item_id(id, name, unit)")
          .eq("clinic_id", clinicId)
          .gte("created_at", dateFrom.toISOString())
          .lte("created_at", dateTo.toISOString())
          .is("deleted_at", null),

        supabase
          .from("product_stock")
          .select("id, item_id, quantity, min_expiry_date, has_expiring_items, items:item_id(id, name, unit)")
          .eq("clinic_id", clinicId)
          .is("deleted_at", null),
      ]);

      if (movementsRes.error) throw movementsRes.error;
      if (stockRes.error) throw stockRes.error;

      const movementRowsByItem: Record<string, StockMovementSummaryRow> = {};
      let quantityIn = 0;
      let quantityOut = 0;
      let adjustments = 0;

      ((movementsRes.data ?? []) as StockMovementSelect[]).forEach((movement) => {
        const quantity = Math.abs(Number(movement.quantity ?? 0));
        const type = movement.type.toUpperCase();
        const itemId = movement.item_id;
        if (!movementRowsByItem[itemId]) {
          movementRowsByItem[itemId] = {
            itemId,
            itemName: movement.items?.name ?? "Item",
            unit: movement.items?.unit ?? null,
            quantityIn: 0,
            quantityOut: 0,
            adjustments: 0,
            movementCount: 0,
          };
        }

        movementRowsByItem[itemId].movementCount += 1;
        if (type === "ENTRADA") {
          quantityIn += quantity;
          movementRowsByItem[itemId].quantityIn += quantity;
        } else if (type === "SAIDA") {
          quantityOut += quantity;
          movementRowsByItem[itemId].quantityOut += quantity;
        } else {
          adjustments += quantity;
          movementRowsByItem[itemId].adjustments += quantity;
        }
      });

      const stockRows = ((stockRes.data ?? []) as ProductStockSelect[]).map((stock) => ({
        stockId: stock.id,
        itemId: stock.item_id,
        itemName: stock.items?.name ?? "Item",
        unit: stock.items?.unit ?? null,
        quantity: Number(stock.quantity ?? 0),
        minExpiryDate: stock.min_expiry_date ? new Date(stock.min_expiry_date) : null,
        hasExpiringItems: stock.has_expiring_items ?? false,
      }));

      const lowStockRows = stockRows
        .filter((stock) => stock.quantity <= LOW_STOCK_THRESHOLD)
        .sort((a, b) => a.quantity - b.quantity || a.itemName.localeCompare(b.itemName));

      const currentStockByItemId: Record<string, number> = {};
      stockRows.forEach((s) => { currentStockByItemId[s.itemId] = s.quantity; });

      return {
        quantityIn,
        quantityOut,
        adjustments,
        lowStockCount: lowStockRows.length,
        criticalStockCount: stockRows.filter((stock) => stock.quantity <= 0).length,
        movementRows: Object.values(movementRowsByItem)
          .sort((a, b) => b.quantityOut - a.quantityOut || b.movementCount - a.movementCount)
          .slice(0, 100),
        lowStockRows,
        currentStockByItemId,
      };
    },
    enabled: !!clinicId && !!dateFrom && !!dateTo,
  });
}
